import {
  rtkApi,
  GET_ENROLLMENTS,
  GET_ENROLLMENT_TERMS,
  GET_ENROLLMENT_INTAKES,
  GET_ENROLLMENT_TERM,
  GET_MODULE_LESSONS,
  GET_ENROLLMENT_LESSON,
} from '@/shared/api';
import { SuccessApiResponse } from '@/shared/types/common';
import { createFormData } from '@/shared/lib/utils/createFormData';
import {
  EnrollmentListItem,
  IEnrollmentIntakeResponse,
  IEnrollmentResource,
  IEnrollmentResponse,
  IEnrollmentTermsRequest,
  IEnrollmentTermsResponse,
  IEnrollmentTermListResponse,
  EnrollmentTermStatus,
  IModuleLessonsRequest,
  IModuleLessonsResource,
  IEnrollmentLessonRequest,
  IEnrollmentLessonResource,
  IEnrollmentLessonNoteRequest,
  IEnrollmentLessonNoteResponse,
} from './types';

const enrollmentApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getAllEnrollments: build.query<IEnrollmentResource[], void>({
      query: () => '/student/education/enrollments',
      transformResponse: (response: SuccessApiResponse<IEnrollmentResponse>) =>
        response.data.enrollments,
      providesTags: [GET_ENROLLMENTS],
    }),
    // status=active also returns enrolled courses waiting for a group or a schedule (kind: 'awaiting')
    getEnrollmentTermItems: build.query<EnrollmentListItem[], EnrollmentTermStatus | void>({
      query: (status) => ({
        url: '/student/education/enrollment-terms',
        params: status ? { status } : undefined,
      }),
      // extraOptions: {
      //   skipAuthRedirect: true,
      // },
      transformResponse: (response: SuccessApiResponse<IEnrollmentTermListResponse>) =>
        response.data.items,
      providesTags: [GET_ENROLLMENT_TERMS],
    }),
    getEnrollmentIntakes: build.query<IEnrollmentIntakeResponse, string>({
      query: (enrollmentId) => `/student/education/enrollments/${enrollmentId}/intake-details`,
      transformResponse: (response: SuccessApiResponse<IEnrollmentIntakeResponse>) => response.data,
      providesTags: [GET_ENROLLMENT_INTAKES],
    }),
    getEnrollmentTerms: build.query<IEnrollmentTermsResponse, IEnrollmentTermsRequest>({
      query: ({ enrollmentId, intakeTermId }) =>
        `/student/education/enrollments/${enrollmentId}/terms/${intakeTermId}`,
      transformResponse: (response: SuccessApiResponse<IEnrollmentTermsResponse>) => response.data,
      providesTags: [GET_ENROLLMENT_TERM],
    }),
    getModuleLessons: build.query<IModuleLessonsResource, IModuleLessonsRequest>({
      query: ({ intakeModuleId, groupNumber }) =>
        `/student/education/modules/${intakeModuleId}/group/${groupNumber}/lessons`,
      transformResponse: (response: SuccessApiResponse<IModuleLessonsResource>) => response.data,
      providesTags: [GET_MODULE_LESSONS],
    }),
    getEnrollmentLesson: build.query<IEnrollmentLessonResource, IEnrollmentLessonRequest>({
      query: ({ enrollmentId, intakeTermId, groupLessonId, groupExamResubmissionId }) => {
        const baseUrl = `/student/education/enrollments/${enrollmentId}/terms/${intakeTermId}/lessons`;
        if (groupLessonId) {
          return `${baseUrl}?groupLessonId=${groupLessonId}`;
        }
        return `${baseUrl}?groupExamResubmissionId=${groupExamResubmissionId}`;
      },
      transformResponse: (response: SuccessApiResponse<IEnrollmentLessonResource>) => response.data,
      providesTags: [GET_ENROLLMENT_LESSON],
    }),
    createEnrollmentLessonNote: build.mutation<
      IEnrollmentLessonNoteResponse,
      IEnrollmentLessonNoteRequest
    >({
      query: ({ groupLessonId, groupExamResubmissionId, message, attachments }) => {
        const payload: { message: string; attachments?: File[] } = { message };
        if (attachments?.length) {
          payload.attachments = attachments;
        }
        const url = groupLessonId
          ? `/student/education/lessons/notes?groupLessonId=${groupLessonId}`
          : `/student/education/lessons/notes?groupExamResubmissionId=${groupExamResubmissionId}`;
        return {
          url,
          method: 'POST',
          body: createFormData(payload),
        };
      },
      transformResponse: (response: SuccessApiResponse<IEnrollmentLessonNoteResponse>) =>
        response.data,
      invalidatesTags: [GET_ENROLLMENT_LESSON],
    }),
  }),
});

export const {
  useGetAllEnrollmentsQuery,
  useGetEnrollmentTermItemsQuery,
  useGetEnrollmentIntakesQuery,
  useGetEnrollmentTermsQuery,
  useGetModuleLessonsQuery,
  useGetEnrollmentLessonQuery,
  useCreateEnrollmentLessonNoteMutation,
} = enrollmentApi;
